import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import { AddIcon, ChevronLeftIcon, ListIcon, SearchIcon } from '@treams/treams-icons';
import { Button, Input, Lazy, Select, Sidebar } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';

import Drawer from './Drawer';

export default function EmployeeSettingsSidebar(props: {
	visible?: boolean;
	onClose?: () => void;
}) {
	const { t } = useTranslation();

	const [animated, setAnimated] = useState(false);

	useEffect(() => setAnimated(true), []);

	const [query, setQuery] = useState('');
	const [status, setStatus] = useState(graphql.UserStatus.Active);

	const search = useSearch(query, { status });

	const results = search.results.sort((a, b) =>
		`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
	);

	return (
		<Drawer
			helicopter={false}
			onClose={props.onClose}
			position={props.visible && animated ? 'active' : 'right'}
		>
			<Button
				title={t('settings.employees.back')}
				type="secondary"
				icon={<ChevronLeftIcon />}
				link="/settings"
				mb={4}
			/>
			<Sidebar.Item
				small
				icon={<ListIcon />}
				title={t('settings.employees.bulk')}
				exact
				path="/settings/employees"
			/>
			<Sidebar.Item
				small
				icon={<AddIcon />}
				title={t('settings.employees.create.title')}
				exact
				path="/settings/employees/new"
			/>
			<Sidebar.Separator />
			<Box
				flexShrink={1}
				flexGrow={1}
				style={{ overflowY: 'auto', margin: '-16px -8px', padding: '4px 8px' }}
			>
				<Flex my={2}>
					<Box flexGrow={1} flexShrink={1} flexBasis="60%">
						<Input
							icon={<SearchIcon />}
							small
							value={query}
							onChange={setQuery}
							placeholder={t('settings.employees.search.placeholder')}
						/>
					</Box>
					<Box flexGrow={1} flexShrink={1} flexBasis="40%" ml={2}>
						<Select
							small
							search={false}
							selectedValue={status}
							options={[
								{ value: graphql.UserStatus.Active, title: t('settings.employees.status.active') },
								{
									value: graphql.UserStatus.Inactive,
									title: t('settings.employees.status.inactive'),
								},
								{
									value: graphql.UserStatus.Archived,
									title: t('settings.employees.status.archived'),
								},
							]}
							onChange={(value) => setStatus(value! as graphql.UserStatus)}
						/>
					</Box>
				</Flex>

				{_.chunk(results ?? [], 10).map((page, index) => (
					<Lazy key={index} estimatedHeight={42 * page.length} debounce={10}>
						{page.map((user) => (
							<Sidebar.User
								key={user.id}
								user={user}
								query={query}
								to={`/settings/employees/${user.id}`}
							/>
						))}
					</Lazy>
				))}
			</Box>
		</Drawer>
	);
}
